import { getThemeData, slideDown, slideUp } from 'theme-utilities';
import { subscribeToMailingList } from './modules/subscribe';

const selectors = {
	form: '.inxmail-subscription-form',
	generalStatusMessage: '.status-message--general',
	inputEmail: 'input[name="inxmail_recipient"]',
	inputSalesPermission: 'input[name="sales_permission"]',
	inputTrackingPermission: 'input[name="tracking_permission"]',
	mailingList: '.mailing-list',
	statusMessage: '.status-message',
	loader: '.inxmail-loader',
};

document.querySelectorAll(selectors.form).forEach(async (form) => {
	let themeData;
	let messages;

	const permissionCheckboxes = {
		tracking: form.querySelector(selectors.inputTrackingPermission),
		sales: form.querySelector(selectors.inputSalesPermission),
	};

	const showLoader = () => form.querySelector(selectors.loader)?.classList.add('show');
	const hideLoader = () => form.querySelector(selectors.loader)?.classList.remove('show');

	const showMessage = (messageType, mailingListID = null) => {
		if (messages[messageType]) {
			let messageEl = form.querySelector(selectors.generalStatusMessage);

			// Show message for specific mailing list in case of multiple mailing lists.
			if (mailingListID && form.querySelectorAll(selectors.mailingList).length) {
				messageEl = form.querySelector(`${selectors.mailingList}[data-list-id="${mailingListID}"] ${selectors.statusMessage}`);
			}

			messageEl.setAttribute('data-type', messageType);
			messageEl.textContent = messages[messageType];
			slideDown(messageEl);
		}
	};

	const hideMessage = () => {
		const messageEl = form.querySelector(selectors.generalStatusMessage);
		if (!messageEl.hidden) {
			messageEl.removeAttribute('data-type');
			messageEl.textContent = '';
			slideUp(messageEl);
		}
	};

	const onFormSubmit = async (e) => {
		e.preventDefault();

		const emailAddress = form.querySelector(selectors.inputEmail).value;

		const formData = new FormData(form);

		if (formData.getAll('inxmail_mailing_lists[]').length === 0) {
			// If multiple mailing lists are available, at least one must be selected.
			showMessage('missing_selection');
		} else {
			// Check if the user's email address is subscribed to the selected mailing list(s).
			hideMessage();
			showLoader();

			try {
				formData.append('action', 'inxmail_recipient');
				const result = await fetch(themeData.ajax_route, {
					method: 'POST',
					body: formData,
				});
				const data = await result.json();

				// Process all the responses of the Inxmail REST API.
				for (let [listID, listData] of Object.entries(data)) {
					listID = parseInt(listID);
					listData = JSON.parse(listData);

					// Check if the user is subscribed to that mailing list.
					const isSubscribed = '_embedded' in listData;
					if (isSubscribed) {
						// If the user is already subscribed to this mailing list, show a message.
						showMessage('member', listID);
					} else {
						// If the user is not subscribed to this mailing list, subscribe them.
						let trackingPermission;
						if (permissionCheckboxes.tracking) {
							trackingPermission = permissionCheckboxes.tracking.checked;
						}

						const recipientData = {};
						if (permissionCheckboxes.sales) {
							recipientData['Kontakterlaubnis'] = permissionCheckboxes.sales.checked;
						}

						const response = await subscribeToMailingList(listID, emailAddress, trackingPermission, recipientData);

						/**
						 * Show a message depending on the user's subscription status.
						 *
						 * @link https://apidocs.inxmail.com/xpro/rest/v1/#subscription-event-types
						 */
						switch (response.result) {
							case 'PENDING_SUBSCRIPTION':
								showMessage('pending', listID);
								break;
							case 'PENDING_SUBSCRIPTION_DONE':
							case 'VERIFIED_SUBSCRIPTION':
							case 'MANUAL_SUBSCRIPTION':
								showMessage('success', listID);
								break;
							case 'DUPLICATE_SUBSCRIPTION':
								showMessage('member', listID);
								break;
							case 'SUBSCRIPTION_TIMED_OUT':
							case 'SUBSCRIPTION_ID_NOT_VALID':
							case 'SUBSCRIPTION_EMAIL_MISSMATCH':
							case 'BLACKLISTED':
							case 'INVALID_ADDRESS_ERROR':
							case 'SUBSCRIPTION_VERIFICATION_BOUNCED':
							case 'SUBSCRIPTION_INTERNAL_ERROR':
							case 'LISTBOMBING_ATTEMPT': {
								let err = `${response.title} (Event Type: ${response.result})`;
								'detail' in response && (err += `\n${response.detail}`);
								throw new Error(err);
							}
						}
					}
				}
			} catch (err) {
				// Show an error message if the subscription fails.
				showMessage('error');
				console.error(err);
			} finally {
				// Hide the loader and reset the form.
				hideLoader();
				form.reset();
			}
		}
	};

	try {
		themeData = await getThemeData();
		messages = JSON.parse(form.getAttribute('data-messages'));
		form.removeAttribute('data-messages');
	} catch (err) {
		console.error(err);
	}

	form.addEventListener('submit', onFormSubmit);
});
